/** The copyright posture (docs/decisions/2026-09-12-copyright-posture.md), stated once, before the first upload. */
import { useState } from "react";
import type { ApiResult } from "../api/client";
import { brand } from "../brand";
import { apiMessage } from "../text/errors";

type Props = {
  version: string;
  acceptTerms: (v: string) => Promise<ApiResult<undefined>>;
  onAccepted: () => Promise<void>;
};

export function TermsScreen({ version, acceptTerms, onAccepted }: Props) {
  const [ticked, setTicked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <main className="narrow">
      <h1>Before you upload anything</h1>
      <section className="prose">
        <p>
          {brand.name} turns a textbook or past paper you already own into study material for you
          alone.
        </p>
        <ul>
          <li>You confirm you own, or are licensed to use, every file you upload.</li>
          <li>
            Uploads are used only to generate your material. Nothing you upload is shown to anyone
            else, shared, or offered for download — not even to you.
          </li>
          <li>Generated material is for your personal study. Do not redistribute it.</li>
          <li>You are 13 or older.</li>
        </ul>
        <p>
          The full <a href="/terms">Terms</a>, <a href="/privacy">Privacy</a> notice and the page{" "}
          <a href="/rights">for rights holders</a> are one click away and open in this tab.
        </p>
        <p className="muted">
          Terms version {version}. Rights holders: {brand.supportEmail}.
        </p>
      </section>
      <label className="check">
        <input type="checkbox" checked={ticked} onChange={(e) => setTicked(e.target.checked)} /> I
        agree
      </label>
      {error && <p role="alert">{error}</p>}
      <button
        disabled={!ticked || busy}
        onClick={async () => {
          setBusy(true);
          const r = await acceptTerms(version);
          if (!r.ok) {
            setError(apiMessage(r.error));
            setBusy(false);
            return;
          }
          await onAccepted();
        }}
      >
        Accept and continue
      </button>
    </main>
  );
}
